<script lang="ts">
export default {
  name: 'ExternalPolicyTargetSummary',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { AlertTriangle, ExternalLink } from '@lucide/vue';
import type { EntityMatch, ExtractionTarget } from '@/types/ai';

// What an extraction that creates a policy is about to do, or already did. Read-only
// everywhere: entities are corrected in the field list beside this, alongside every other
// extracted value, and this states the record those values add up to.
const props = withDefaults(
  defineProps<{
    target: ExtractionTarget;
    entityMatches?: EntityMatch[];
    // Each resolved entity's current value, keyed by field path. Read from the merged
    // fields, so a correction shows here rather than the match it replaced.
    entityValues?: Record<string, string>;
    // Offers a way through to the account's review screen. Off where that screen is
    // already the surrounding page.
    showReviewLink?: boolean;
  }>(),
  { entityMatches: () => [], entityValues: () => ({}), showReviewLink: false },
);

const ENTITY_LABELS: Record<EntityMatch['entityType'], string> = {
  carrier: 'Issuing Company',
  coverage_type: 'Coverage Type',
  wholesaler: 'Wholesaler',
};

const LINE_INDEX = /\[(\d+)\]/;

// A schema can ask for the same entity on the policy and again on each coverage line, so the
// entity type alone does not tell two rows apart -- the path they resolve does.
function entityLabel(match: EntityMatch): string {
  const base = ENTITY_LABELS[match.entityType] ?? match.entityType;
  const line = LINE_INDEX.exec(match.fieldPath);
  return line ? `Line ${Number(line[1]) + 1} ${base}` : base;
}

const accountName = computed(() => props.target.accountName || 'this account');

const headline = computed(() => {
  switch (props.target.submissionStatus) {
    case 'confirmed':
      return props.target.createdPolicyNumber
        ? `Created policy ${props.target.createdPolicyNumber} on ${accountName.value}`
        : `Created a policy on ${accountName.value}`;
    case 'discarded':
      return 'Discarded — no policy was created';
    default:
      return `Creates a new policy on ${accountName.value}`;
  }
});

const subhead = computed(() => {
  switch (props.target.submissionStatus) {
    case 'confirmed':
      return 'This extraction has already been confirmed.';
    case 'discarded':
      return 'The binder stays on the account and can be scanned again.';
    default:
      return 'Nothing existing is changed — confirming adds a policy from this binder.';
  }
});

const isOpen = computed(
  () => props.target.submissionStatus !== 'confirmed' && props.target.submissionStatus !== 'discarded',
);

// The policy line stores the child entity a match names, so that is what the broker is
// approving; the group it rolls up to is shown alongside it.
const resolvedEntities = computed(() =>
  props.entityMatches.map((match) => {
    const resolved = match.matchedName;
    return {
      fieldPath: match.fieldPath,
      label: entityLabel(match),
      entityType: match.entityType,
      sourceText: match.sourceText,
      resolved,
      // Only meaningful while the match still stands: a correction may roll up elsewhere,
      // and the summary has no reference data to look that up with.
      groupName: props.entityValues[match.fieldPath] ? undefined : match.groupName,
      unresolved: match.status === 'unmatched',
      // What will be created: the field's current value, which is the reviewer's
      // correction when they made one and the match otherwise.
      effective: props.entityValues[match.fieldPath] || resolved || '',
    };
  }),
);

const unresolvedCount = computed(() => resolvedEntities.value.filter((e) => !e.effective).length);

const reviewRoute = computed(() => ({
  name: 'account-external-policy-binder-review',
  params: { id: String(props.target.accountId), submissionId: String(props.target.submissionId) },
}));

const policyRoute = computed(() => ({ path: `/account/${props.target.accountId}/policy-detail`, query: { ext: 'true' } }));
</script>

<template>
  <div data-testid="external-policy-target-summary">
    <p class="text-sm font-medium">{{ headline }}</p>
    <p class="mt-0.5 text-xs text-slate-600">{{ subhead }}</p>

    <div
      v-if="resolvedEntities.length > 0"
      class="mt-3 flex flex-col gap-2">
      <div
        v-for="entity in resolvedEntities"
        :key="entity.fieldPath"
        class="text-xs"
        :data-testid="`resolved-${entity.fieldPath}`">
        <span class="text-slate-500">{{ entity.label }}:</span>
        <span
          v-if="!entity.effective"
          class="ml-1 text-amber-700">
          not matched
        </span>
        <template v-else>
          <span class="ml-1 font-medium text-slate-700">{{ entity.effective }}</span>
          <span
            v-if="entity.groupName && entity.groupName !== entity.effective"
            class="text-slate-500">
            (rolls up to {{ entity.groupName }})
          </span>
        </template>
      </div>
    </div>

    <div
      v-if="unresolvedCount > 0 && isOpen"
      class="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
      <AlertTriangle class="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        {{ unresolvedCount }} value{{ unresolvedCount > 1 ? 's' : '' }} the scan could not match to platform data.
      </span>
    </div>

    <router-link
      v-if="showReviewLink && isOpen && target.submissionId"
      :to="reviewRoute"
      class="mt-3 flex items-center gap-1 text-xs text-secondary hover:underline"
      data-testid="open-binder-review">
      Review and create on {{ accountName }} <ExternalLink :size="12" />
    </router-link>

    <router-link
      v-else-if="showReviewLink && target.createdPolicyId"
      :to="policyRoute"
      class="mt-3 flex items-center gap-1 text-xs text-secondary hover:underline"
      data-testid="open-created-policy">
      View the policy on {{ accountName }} <ExternalLink :size="12" />
    </router-link>
  </div>
</template>
